import React, {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import styles from "./inviteModal.module.scss";
import LocalConfirmation from "../../../../components/local-confirmation/LocalConfirmation";
import DeleteIcon from "../../../../assets/icons/people_delete.svg?react";
import Modal from "../../../../components/modal/Modal";
import {
  TextInput,
  TextInputValue,
} from "../../../../components/text-input/TextInput";
import Select from "../../../../components/select/Select";
import Button from "../../../../components/button/Button";
import Checkbox from "../../../../components/checkbox/Checkbox";
import {
  isEmptyObject,
  validateEmail,
  validateIsEmpty,
} from "../../../../utils/validation";
import { gql, useMutation } from "@apollo/client";
import { querySchoolUsers } from "../AdminUsers";
import { User, SelectOption, UserRoleEnum } from "../../../../types";
import { Events } from "../../../../services/Events";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useUser } from "../../../../hooks/useUser";
import { SchoolUsersDocument, UserStatus } from "@src/api/graphql-codegen";
import IconOutline from "src/assets/icons/new-outline-info.svg?react";
import { Popover } from "antd";
import { useToast } from "@src/hooks";

type Props = {
  open: boolean;
  onClose: () => void;
  user?: User;
  schoolOptions: SelectOption[];
  reLoadData?: (v?: any) => Promise<any>;
  handleReload?: () => void;
};

const emptyErrors = {
  firstName: "",
  lastName: "",
  email: "",
  school: "",
};

const mutationCreateInvite = gql`
  mutation CreateInvite($input: UserInviteInput!) {
    inviteUserAsEducator(input: $input) {
      id
      email
      firstName
      lastName
      status
      role
      schools {
        id
        name
      }
    }
  }
`;

const mutationEditInvite = gql`
  mutation UpdateInvite($input: UserInviteEditInput!) {
    inviteUserAsEducatorEdit(input: $input) {
      id
      email
      firstName
      lastName
      status
      role
      schools {
        id
        name
      }
    }
  }
`;

type RemoveUserResponse = {
  superAdminRemoveUser: boolean;
};

const RemoveUserMutation = gql`
  mutation RemoveUser($input: UserIDInput!) {
    superAdminRemoveUser(input: $input)
  }
`;

type MutationCreteInviteResponse = {
  inviteUserAsEducator: User;
};

type MutationUpdateInviteResponse = {
  inviteUserAsEducatorEdit: User;
};

function InviteModal(props: Props) {
  const { open, onClose, user, schoolOptions, handleReload } = props;

  const { trackEvent } = useAnalytics();
  const { successToast, errorToast } = useToast();

  const [firstName, setFirstName] = useState<TextInputValue>("");
  const [lastName, setLastName] = useState<TextInputValue>("");
  const [email, setEmail] = useState<TextInputValue>("");
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [error, setError] = useState(emptyErrors);
  const [beError, setBeError] = useState("");
  const isEdit = !!user;
  const [me] = useUser();
  const [removeConfirmation, setRemoveConfirmation] = useState(false);
  const [school, setSchool] = useState("");
  const [removeUser, { loading: removeLoading }] =
    useMutation<RemoveUserResponse>(RemoveUserMutation, {
      onError(err) {
        setBeError(err.message);
      },
      onCompleted(data) {
        if (data.superAdminRemoveUser) {
          trackEvent(Events.actions.userRemoved, Events.mappers.user(user));
          successToast("Account Successfully Deleted!");
          clearFields();
          onClose();
          handleReload?.();
        } else {
          errorToast("Remove failed, Please try again after refresh.");
        }
      },
      update(cache, response, { variables }) {
        if (response.data?.superAdminRemoveUser && variables) {
          cache.updateQuery(
            {
              query: querySchoolUsers,
            },
            (data) => ({
              schoolUsers:
                data?.schoolUsers.filter(
                  (u: User) => u.id !== variables.userId,
                ) ?? [],
            }),
          );
        }
      },
    });

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setEmail(user?.email ?? "");
    setSchool(user?.schools?.[0]?.id ?? "");
    if (
      user?.role === UserRoleEnum.Admin ||
      user?.role === UserRoleEnum.DistrictAdmin
    ) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const [createInvite, { loading }] = useMutation<MutationCreteInviteResponse>(
    mutationCreateInvite,
    {
      onError(e) {
        setBeError(e.message);
      },
      update(cache, response) {
        const u = response.data?.inviteUserAsEducator;
        if (u) {
          cache.updateQuery(
            {
              query: SchoolUsersDocument,
            },
            (data) => ({
              schoolUsers: [u, ...(data?.schoolUsers || [])],
            }),
          );
          window.location.reload();
        }
        successToast(
          `Your Invitation has been successfully sent to ${response.data?.inviteUserAsEducator.firstName} ${response.data?.inviteUserAsEducator.lastName}!`,
        );
        clearFields();
        onClose();
        handleReload?.();
      },
    },
  );

  const [updateInvite, { loading: updateLoading }] =
    useMutation<MutationUpdateInviteResponse>(mutationEditInvite, {
      onError(e) {
        setBeError(e.message);
      },
      update(cache, response) {
        const u = response.data?.inviteUserAsEducatorEdit;
        if (u) {
          cache.writeFragment({
            id: `User:${u.id}`,
            data: {
              ...u,
              schools: [...(u?.schools || []), { id: school }],
            },
            fragment: gql`
              fragment UpdateUser on User {
                firstName
                lastName
                email
                role
                schools
              }
            `,
          });

          if (u.role === UserRoleEnum.Admin) {
            setIsAdmin(true);
          }
        }
        successToast(
          `You have successfully updated ${firstName} ${lastName}'s Account!`,
        );
        clearFields();
        onClose();
        handleReload?.();
      },
    });

  const handleChangeText =
    (
      setter: Dispatch<SetStateAction<TextInputValue>>,
      key: keyof typeof emptyErrors,
    ) =>
    (value: TextInputValue) => {
      setter(value);
      setError({
        ...error,
        [key]: "",
      });
    };

  const clearFields = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setBeError("");
    setError(emptyErrors);
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const validate = () => {
    const errors: any = {};

    if (validateEmail(email)) {
      errors.email = validateEmail(email);
    }

    if (validateIsEmpty(firstName)) {
      errors.firstName = "First name must not be empty.";
    }

    if (validateIsEmpty(lastName)) {
      errors.lastName = "Last name must not be empty.";
    }

    if (me?.role === UserRoleEnum.DistrictAdmin && validateIsEmpty(school)) {
      errors.school = "School must not be empty.";
    }

    return errors;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const errors = validate();

    if (isEmptyObject(errors)) {
      setError(emptyErrors);
      setBeError("");

      if (isEdit) {
        if (me?.role === UserRoleEnum.DistrictAdmin) {
          updateInvite({
            variables: {
              input: {
                firstName,
                lastName,
                email,
                id: user?.id,
                resendInvite: false,
                customer: user?.customer?.id,
                adminId: user?.admin,
                customerName: user?.customer?.name,
                school,
                admin: isAdmin,
              },
            },
          });
        } else {
          updateInvite({
            variables: {
              input: {
                firstName,
                lastName,
                email,
                id: user?.id,
                resendInvite: false,
                customer: user?.customer?.id,
                adminId: user?.admin,
                customerName: user?.customer?.name,
                admin: isAdmin,
              },
            },
          });
        }
      } else {
        if (me?.role === UserRoleEnum.DistrictAdmin) {
          createInvite({
            variables: {
              input: {
                firstName,
                lastName,
                email,
                school,
              },
            },
          });
        } else {
          createInvite({
            variables: {
              input: {
                firstName,
                lastName,
                email,
              },
            },
          });
        }
      }
    } else {
      setError(errors);
    }
  };

  const handleUpdateInvite = () => {
    const errors = validate();

    if (isEmptyObject(errors)) {
      setError(emptyErrors);
      setBeError("");

      if (me?.role === UserRoleEnum.DistrictAdmin) {
        updateInvite({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              id: user?.id,
              resendInvite: true,
              customer: user?.customer?.id,
              adminId: user?.admin,
              customerName: user?.customer?.name,
              school,
              admin: isAdmin,
            },
          },
        });
      } else {
        updateInvite({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              id: user?.id,
              resendInvite: true,
              customer: user?.customer?.id,
              adminId: user?.admin,
              customerName: user?.customer?.name,
              admin: isAdmin,
            },
          },
        });
      }
    } else {
      setError(errors);
    }
  };

  const handleRemove = () => {
    user?.id &&
      removeUser({
        variables: {
          input: {
            id: user.id,
          },
        },
      });
  };

  const handleConfirmRemove = () => {
    handleRemove();
    setRemoveConfirmation(false);
  };

  const handleCancel = () => {
    setRemoveConfirmation(false);
  };

  const handleShowConfirm = () => {
    setRemoveConfirmation(true);
  };

  const handleChangeOption = useCallback(
    (value: SelectOption) => {
      setSchool(value.value);
      setError({
        ...error,
        school: "",
      });
    },
    [error],
  );

  const handleChangeAdmin = useCallback((v: boolean) => {
    setIsAdmin(v);
  }, []);

  const isLoading = useMemo(
    () => loading || updateLoading || removeLoading,
    [loading, removeLoading, updateLoading],
  );
  const content = (
    <div className={styles.cardBack}>
      <IconOutline className={styles.infoicon} width={44} height={44} />
      <div className={styles.hoverCardText}>
        <div className={styles.span1}>
          OP Administrators can manage all users from their school. This
          includes inviting staff, removing staff, and viewing staff usage.
        </div>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={handleClose} closeOnOverlayClick={false}>
      <div className={styles.title}>
        {isEdit ? "Edit User Information" : "Invite user"}
      </div>
      <div className={styles.deleteWrapper}>
        {isEdit && (
          <LocalConfirmation
            onConfirm={handleConfirmRemove}
            onCancel={handleCancel}
            open={removeConfirmation}
          >
            <Button
              disabled={isLoading}
              type={"button"}
              onClick={handleShowConfirm}
              className={styles.removeBtn}
              color={"red"}
              size={"extraSmall"}
              label={"Delete"}
              leftIcon={<DeleteIcon />}
            />
          </LocalConfirmation>
        )}
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.content}>
          <TextInput
            allowSpecialCharacters
            hasError={!!error.firstName}
            subTitle={error.firstName}
            placeholder={"First Name"}
            inline={true}
            label={"First name"}
            id={"firstName"}
            value={firstName}
            onChange={handleChangeText(setFirstName, "firstName")}
          />
          <TextInput
            allowSpecialCharacters
            hasError={!!error.lastName}
            subTitle={error.lastName}
            placeholder={"Last Name"}
            inline={true}
            label={"Last name"}
            id={"lastName"}
            value={lastName}
            onChange={handleChangeText(setLastName, "lastName")}
          />
          <TextInput
            allowSpecialCharacters
            hasError={!!error.email}
            subTitle={error.email}
            placeholder={"Email"}
            inline={true}
            label={"Email"}
            id={"email"}
            value={email}
            onChange={handleChangeText(setEmail, "email")}
          />
          {me?.role === UserRoleEnum.DistrictAdmin ? (
            <Select
              error={!!error.school}
              subTitle={error.school}
              disabled={schoolOptions.length === 0}
              label={"School"}
              options={schoolOptions}
              value={school}
              onChange={handleChangeOption}
              inline={true}
              searchAutoFocus
            />
          ) : null}
          <div className={styles.gridRowWrapper}>
            <label> &nbsp; </label>
            <div className={styles.gridContent}>
              <Checkbox
                id={"isAdmin"}
                checked={isAdmin}
                onChange={handleChangeAdmin}
              >
                <div className={styles.checkboxLabel}>
                  OP Administrator
                  <Popover
                    overlayClassName="custom-info"
                    placement="bottom"
                    content={content}
                  >
                    <IconOutline
                      className={styles.labelInfoIcon}
                      width={24}
                      height={24}
                    />
                  </Popover>
                </div>
              </Checkbox>
            </div>
          </div>
          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <div />
            <Button
              disabled={isLoading}
              type={"submit"}
              label={isEdit ? "Save" : "Send Invite"}
            />
            {isEdit && user.status !== UserStatus.ACCEPTED && (
              <>
                <div />
                {user?.status === "Accepted" ? (
                  ""
                ) : (
                  <Button
                    onClick={handleUpdateInvite}
                    className={styles.resendBtn}
                    disabled={isLoading}
                    type={"button"}
                    label={"Save and Resend the Invite"}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default InviteModal;
